import { describirFrecuencia, NOMBRE_ROL, type RolGlobal } from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { notFound, useRouteContext } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { conAviso, queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { type FormularioDeUsuario, FormularioUsuario } from "../componentes/FormularioUsuario.tsx";
import { Modal } from "../componentes/ModalFirma.tsx";
import {
  Aviso,
  Boton,
  CABECERA,
  CLASE_ENTRADA,
  EstadoVacio,
  Pildora,
  Tabla,
  TD,
  TH,
} from "../componentes/ui.tsx";
import type { ListaDeUsuarios, UsuarioAdministrado } from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";

/**
 * "Usuarios": solo un administrador. Da de alta a las personas con su puesto (del listado
 * aprobado DIR-ESP-004; el nombre va a las firmas), su rol, sus procesos, sus diligencias (qué
 * formatos de registro llena y cada cuánto, 2026-10-20) y si pueden entrar desde fuera de la
 * oficina; desactiva a quien se va (sus firmas quedan) y restablece contraseñas. La contraseña
 * temporal se muestra una sola vez: el administrador se la entrega en persona. Los nombres de los
 * roles salen de `NOMBRE_ROL` (packages/shared): se llaman por lo que permiten, no por el área
 * que los usa.
 */

const TONO_ROL: Record<RolGlobal, string> = {
  colaborador: "bg-marino-claro text-marino",
  gerencia: "bg-ambar-fondo text-ambar",
  administrador: "bg-verde-fondo text-verde",
  soporte: "bg-marino-claro text-marino",
};

export function PaginaUsuarios() {
  const { perfil } = useRouteContext({ from: "/app" });
  if (perfil.usuario.rolGlobal !== "administrador") throw notFound();

  const trpc = useTRPC();
  const lista = useQuery(trpc.usuarios.listar.queryOptions());
  const [busqueda, setBusqueda] = useState("");
  const [conInactivos, setConInactivos] = useState(false);
  const [formulario, setFormulario] = useState<FormularioDeUsuario | null>(null);
  const [contrasena, setContrasena] = useState<{ nombre: string; valor: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recargar = () => queryClient.invalidateQueries();

  const filtrados = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return (lista.data?.usuarios ?? []).filter(
      (u) =>
        (conInactivos || u.activo) &&
        (texto === "" ||
          u.nombre.toLowerCase().includes(texto) ||
          u.correo.toLowerCase().includes(texto) ||
          u.puesto.toLowerCase().includes(texto)),
    );
  }, [lista.data, busqueda, conInactivos]);

  const inactivos = lista.data?.usuarios.filter((u) => !u.activo).length ?? 0;

  return (
    <div className="max-w-[1100px]">
      <Encabezado
        overline="Administración"
        titulo="Usuarios"
        acciones={<Boton onClick={() => setFormulario({ modo: "crear" })}>Nuevo usuario</Boton>}
      />
      <p className="mb-4 text-[12.5px] text-texto-3">
        Nadie se registra solo, ni con Google: entra quien está aquí, con el correo que diste de
        alta. El nombre y el puesto salen en las firmas y en los sellos. Quien se va no se borra: se
        desactiva, pierde el acceso en el acto y sus firmas quedan.
      </p>

      {error && <Aviso className="mb-4">{error}</Aviso>}

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, correo o puesto"
          aria-label="Buscar"
          className={`${CLASE_ENTRADA} max-w-[360px]`}
        />
        <label className="flex items-center gap-2 text-[12.5px] text-texto-2">
          <input
            type="checkbox"
            checked={conInactivos}
            onChange={(e) => setConInactivos(e.target.checked)}
          />
          Mostrar desactivados{inactivos > 0 ? ` (${inactivos})` : ""}
        </label>
      </div>

      {lista.isPending && <EstadoVacio>Cargando…</EstadoVacio>}
      {lista.data && filtrados.length === 0 && (
        <EstadoVacio>Ninguna persona coincide con la búsqueda.</EstadoVacio>
      )}
      {lista.data && filtrados.length > 0 && (
        <Tabla minimo={980}>
          <thead>
            <tr className={CABECERA}>
              <th className={TH}>Persona</th>
              <th className={TH}>Rol</th>
              <th className={TH}>Procesos</th>
              <th className={TH}>Diligencias</th>
              <th className={TH}>Desde fuera</th>
              <th className={TH}>Firma</th>
              <th className={TH} />
            </tr>
          </thead>
          <tbody>
            {filtrados.map((u) => (
              <FilaDeUsuario
                key={u.id}
                usuario={u}
                procesos={lista.data.procesos}
                formatos={lista.data.formatos}
                esYo={u.id === perfil.usuario.id}
                onEditar={() => setFormulario({ modo: "editar", usuario: u })}
                onContrasena={(valor) => setContrasena({ nombre: u.nombre, valor })}
                onError={setError}
              />
            ))}
          </tbody>
        </Tabla>
      )}

      {formulario && lista.data && (
        <FormularioUsuario
          formulario={formulario}
          procesos={lista.data.procesos}
          puestos={lista.data.puestos}
          formatos={lista.data.formatos}
          onCerrar={() => setFormulario(null)}
          onCreado={(nombre, valor) => {
            setFormulario(null);
            setContrasena({ nombre, valor });
            recargar();
          }}
          onGuardado={() => {
            setFormulario(null);
            recargar();
          }}
        />
      )}

      {contrasena && (
        <ContrasenaTemporal
          nombre={contrasena.nombre}
          valor={contrasena.valor}
          onCerrar={() => setContrasena(null)}
        />
      )}
    </div>
  );
}

function FilaDeUsuario({
  usuario: u,
  procesos,
  formatos,
  esYo,
  onEditar,
  onContrasena,
  onError,
}: {
  usuario: UsuarioAdministrado;
  procesos: ListaDeUsuarios["procesos"];
  formatos: ListaDeUsuarios["formatos"];
  esYo: boolean;
  onEditar: () => void;
  onContrasena: (valor: string) => void;
  onError: (m: string | null) => void;
}) {
  const trpc = useTRPC();
  const desactivar = useMutation(trpc.usuarios.desactivar.mutationOptions(conAviso(onError)));
  const reactivar = useMutation(trpc.usuarios.reactivar.mutationOptions(conAviso(onError)));
  const restablecer = useMutation(
    trpc.usuarios.restablecerContrasena.mutationOptions({
      onSuccess: (r) => {
        onError(null);
        onContrasena(r.contrasenaTemporal);
      },
      onError: (e) => onError(mensajeDe(e)),
    }),
  );
  const ocupado = desactivar.isPending || reactivar.isPending || restablecer.isPending;
  const codigoDe = (id: string) => procesos.find((p) => p.id === id)?.codigo ?? "?";
  const formatoDe = (id: string) => formatos.find((f) => f.id === id);

  return (
    <tr className={`border-b border-borde/60 last:border-0 ${u.activo ? "" : "opacity-60"}`}>
      <td className={TD}>
        <div className="font-semibold">
          {u.nombre}
          {esYo && <span className="ml-1.5 text-[11px] font-normal text-texto-3">(tú)</span>}
          {!u.activo && (
            <Pildora tono="bg-gris-fondo text-gris" className="ml-2">
              Desactivado
            </Pildora>
          )}
        </div>
        <div className="text-[11.5px] text-texto-3">{u.puesto || "Sin puesto"}</div>
        <div className="text-[11.5px] text-texto-3">{u.correo}</div>
      </td>
      <td className={TD}>
        <Pildora tono={TONO_ROL[u.rolGlobal]}>{NOMBRE_ROL[u.rolGlobal]}</Pildora>
      </td>
      <td className={TD}>
        {u.procesos.length === 0 ? (
          <span className="text-texto-3">—</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {u.procesos.map((p) => (
              <span
                key={p.procesoId}
                title={p.rol === "carga" ? "Carga y modifica" : "Solo consulta"}
                className="inline-flex items-center gap-1.5 rounded-full border border-borde px-2 py-0.5 font-display text-[11px] font-semibold tracking-wide"
              >
                <span
                  className={`size-2 rounded-full ${p.rol === "carga" ? "bg-acceso" : "bg-texto-3/40"}`}
                />
                {codigoDe(p.procesoId)}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className={TD}>
        {u.diligencias.length === 0 ? (
          <span className="text-texto-3">—</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {u.diligencias.map((d) => {
              const f = formatoDe(d.documentoId);
              return (
                <span
                  key={d.documentoId}
                  title={`${f?.nombre ?? ""} · ${describirFrecuencia(d.frecuencia)}`}
                  className="inline-flex items-center rounded-full border border-borde px-2 py-0.5 font-mono text-[11px]"
                >
                  {f?.codigo ?? "?"}
                </span>
              );
            })}
          </div>
        )}
      </td>
      <td className={`${TD} text-texto-2`}>
        {u.accesoExterno ? (
          <>
            Sí
            <div className="text-[11.5px] text-texto-3">
              {u.segundoFactor ? "Con código activado" : "Sin código todavía"}
            </div>
          </>
        ) : (
          "No"
        )}
      </td>
      <td className={`${TD} text-texto-2`}>
        {u.tieneFirma ? "Cargada" : "Sin firma"}
        {u.cargaSinFirmas && (
          <div className="mt-1">
            <Pildora tono="bg-ambar-fondo text-ambar">Sube sin firmas</Pildora>
          </div>
        )}
      </td>
      <td className={`${TD} text-right whitespace-nowrap`}>
        <div className="flex justify-end gap-1.5">
          <Boton variante="secundario" onClick={onEditar} disabled={ocupado}>
            Editar
          </Boton>
          {u.activo && (
            <Boton
              variante="secundario"
              disabled={ocupado}
              onClick={() => {
                if (
                  window.confirm(
                    `¿Restablecer la contraseña de ${u.nombre}? Sus sesiones abiertas se cierran y verás la contraseña nueva una sola vez.`,
                  )
                ) {
                  restablecer.mutate({ id: u.id });
                }
              }}
            >
              Contraseña
            </Boton>
          )}
          {u.activo ? (
            !esYo && (
              <Boton
                variante="peligro"
                disabled={ocupado}
                onClick={() => {
                  if (
                    window.confirm(
                      `¿Desactivar a ${u.nombre}? Pierde el acceso en el acto; sus firmas y documentos quedan.`,
                    )
                  ) {
                    desactivar.mutate({ id: u.id });
                  }
                }}
              >
                Desactivar
              </Boton>
            )
          ) : (
            <Boton disabled={ocupado} onClick={() => reactivar.mutate({ id: u.id })}>
              Reactivar
            </Boton>
          )}
        </div>
      </td>
    </tr>
  );
}

/** La contraseña temporal, una sola vez. Se cierra y no hay forma de volver a verla. */
function ContrasenaTemporal({
  nombre,
  valor,
  onCerrar,
}: {
  nombre: string;
  valor: string;
  onCerrar: () => void;
}) {
  const [copiada, setCopiada] = useState(false);
  async function copiar() {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiada(true);
    } catch {
      setCopiada(false);
    }
  }
  return (
    <Modal titulo={`Contraseña temporal de ${nombre}`} onCerrar={onCerrar}>
      <p className="mb-3 text-[13px] text-texto-2">
        Entrégasela en persona. Se muestra <span className="font-semibold">una sola vez</span>: al
        cerrar esta ventana ya no se puede volver a ver; si se pierde, restablécela de nuevo.
      </p>
      <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-borde bg-fondo px-4 py-3">
        <code className="text-[18px] font-semibold tracking-wider text-marino select-all">
          {valor}
        </code>
        <Boton variante="secundario" onClick={copiar}>
          {copiada ? "Copiada" : "Copiar"}
        </Boton>
      </div>
      <p className="mb-4 text-[12px] text-texto-3">
        La persona entra con su correo y esta contraseña. Si tiene cuenta de Google de SMG, después
        puede entrar con «Entrar con Google» sin escribirla.
      </p>
      <div className="flex justify-end">
        <Boton onClick={onCerrar}>Ya la entregué</Boton>
      </div>
    </Modal>
  );
}
